import { withProductEmail } from './productEventEmail';

/** Sign-in must be happening now, so a later session restore does not count. */
export const SIGNUP_SIGNIN_FRESHNESS_MS = 2 * 60 * 1000;

/**
 * Magic-link accounts are created when the email is sent. The verifying
 * sign-in can lag creation, but a later sign-in is not account creation.
 */
export const SIGNUP_CREATION_WINDOW_MS = 60 * 60 * 1000;

export const SIGNUP_TRACKED_KEY_PREFIX = 'memara_ph_user_signed_up_';

export const INITIAL_UTM_KEYS = [
  '$initial_utm_source',
  '$initial_utm_medium',
  '$initial_utm_campaign',
] as const;

export type InitialUtmKey = (typeof INITIAL_UTM_KEYS)[number];

export interface AccountTimestamps {
  created_at?: string | null;
  last_sign_in_at?: string | null;
}

export interface UserSignedUpProperties {
  method: string;
  email?: string;
  $initial_utm_source?: string;
  $initial_utm_medium?: string;
  $initial_utm_campaign?: string;
}

export interface PostHogSignupClient {
  capture: (event: string, properties?: UserSignedUpProperties) => void;
  get_property?: (key: string) => unknown;
  persistence?: {
    get_initial_props?: () => Record<string, unknown>;
    props?: Record<string, unknown>;
  };
}

const SIGNUP_SUCCESS_EVENTS = new Set([
  'INITIAL_SESSION',
  'SIGNED_IN',
  'SIGNED_UP',
]);

export function isAccountCreationAuthEvent(event: string): boolean {
  return SIGNUP_SUCCESS_EVENTS.has(event);
}

export function signupTrackedStorageKey(userId: string): string {
  return `${SIGNUP_TRACKED_KEY_PREFIX}${userId}`;
}

function parseTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function within(left: number, right: number, windowMs: number): boolean {
  return Math.abs(left - right) < windowMs;
}

/**
 * True only when this auth payload is the successful creation of the account.
 * A restored session or a later sign-in does not qualify.
 */
export function isSuccessfulAccountCreation(
  user: AccountTimestamps,
  now: number = Date.now(),
): boolean {
  const created = parseTime(user.created_at);
  if (created === null) return false;

  if (within(now, created, SIGNUP_SIGNIN_FRESHNESS_MS)) {
    return true;
  }

  const lastSignIn = parseTime(user.last_sign_in_at);
  if (lastSignIn === null) return false;

  return (
    within(now, lastSignIn, SIGNUP_SIGNIN_FRESHNESS_MS) &&
    within(lastSignIn, created, SIGNUP_CREATION_WINDOW_MS)
  );
}

function asUtmValue(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function rawUtmParam(key: InitialUtmKey): string {
  return key.replace('$initial_', '');
}

/**
 * First-touch UTMs stored by the PostHog pageview. Current URL is only a
 * fallback when nothing has been stored yet.
 */
export function readInitialUtm(
  posthog: PostHogSignupClient | null | undefined,
  key: InitialUtmKey,
): string | undefined {
  if (!posthog) {
    return readUtmFromLocation(key);
  }

  const direct = asUtmValue(posthog.get_property?.(key));
  if (direct) return direct;

  const fromInitial = asUtmValue(
    posthog.persistence?.get_initial_props?.()?.[key],
  );
  if (fromInitial) return fromInitial;

  const rawKey = rawUtmParam(key);
  const props = posthog.persistence?.props;
  const fromProps = asUtmValue(props?.[key] ?? props?.[rawKey]);
  if (fromProps) return fromProps;

  return readUtmFromLocation(key);
}

function readUtmFromLocation(key: InitialUtmKey): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return asUtmValue(
    new URLSearchParams(window.location.search).get(rawUtmParam(key)),
  );
}

export function buildUserSignedUpProperties(
  method: string,
  posthog: PostHogSignupClient | null | undefined,
): UserSignedUpProperties {
  const properties: UserSignedUpProperties = { method };
  for (const key of INITIAL_UTM_KEYS) {
    const value = readInitialUtm(posthog, key);
    if (value) {
      properties[key] = value;
    }
  }
  return properties;
}

export function hasTrackedSignup(userId: string): boolean {
  try {
    return window.localStorage.getItem(signupTrackedStorageKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function markSignupTracked(userId: string): void {
  try {
    window.localStorage.setItem(signupTrackedStorageKey(userId), '1');
  } catch {
    // Storage can be blocked. The in-page capture still runs once per call.
  }
}

/** Captures `user_signed_up` once. Returns false when this user was already counted. */
export function captureUserSignedUp(
  posthog: PostHogSignupClient,
  input: { userId: string; method: string; email?: string | null },
): boolean {
  if (!input.userId || hasTrackedSignup(input.userId)) {
    return false;
  }
  markSignupTracked(input.userId);
  posthog.capture(
    'user_signed_up',
    withProductEmail(
      buildUserSignedUpProperties(input.method, posthog),
      input.email,
    ),
  );
  return true;
}
